<script lang="ts" setup>
const { $directus, $readItems } = useNuxtApp()
const { t } = useI18n()

const { dt } = useDirectusTranslation()

const { data } = await useAsyncData(
  'cover_index',
  () => $directus.request($readItems('covers', {
    sort: 'sort',
    filter: {
      status: {
        _eq: 'published',
      },
    },
    fields: [
      'id',
      'slug',
      'listing_image',
      'listing_image_hover',
      {
        listing_translations: ['*'],
      },
    ],
  })) as Promise<{
    id: number
    slug: string
    listing_image: string
    listing_image_hover: string
    categories: string[]
    listing_translations: DirectusTranslation<{
      id: number
      name: string
      teaser_description: {
        text: string
      }[]
    }>
  }[]>,
)

const covers = computed(() => {
  if (!data.value)
    return []
  return data.value
})
</script>

<template>
  <BlockBase>
    <div class="relative">
      <!-- Border -->
      <Container class="absolute inset-0 border border-neutral-darkGray" />
      <!-- Gradient over border -->
      <div class="bg-gradient absolute inset-x-0 top-0 h-350px" />
      <!-- White background -->
      <BlockBase is-light class="absolute inset-x-0 bottom-0 top-350px bg-neutral-white" />
      <Container class="relative min-h-350px px-6rem pt-6rem">
        <div v-auto-animate class="grid grid-cols-1 gap-4rem lg:grid-cols-2 xl:grid-cols-3">
          <p v-if="!covers?.length" class="pb-6rem pt-4rem text-center text-1.6rem lg:col-span-2 xl:col-span-3">
            {{ t('pool.index_empty') }}
          </p>
          <template v-for="cover in covers" :key="cover.id">
            <NuxtLinkLocale
              :to="`/covers/${cover.slug}`"
              class="group relative bg-white text-black"
            >
              <div class="relative overflow-hidden">
                <NuxtImg
                  loading="lazy"
                  provider="directus" :src="cover.listing_image" height="400" width="400" class="block h-400px w-full object-cover transition-300 group-hover:scale-105"
                />
                <div class="absolute inset-0 h-400px scale-105 object-cover opacity-0 transition-300 group-hover:scale-100 group-hover:opacity-100">
                  <NuxtImg
                    loading="lazy"
                    provider="directus" height="400" width="400" class="h-full w-full object-cover" :src="cover.listing_image_hover"
                  />
                </div>
              </div>
              <div class="absolute left-0 top-0 w-full flex">
                <div class="grid h-6rem place-items-center bg-neutral-white px-2rem transition group-hover:bg-neutral-white/60">
                  <h3 class="whitespace-nowrap text-1.6rem text-neutral-gray font-title transition-all group-hover:text-neutral-black">
                    {{ dt(cover.listing_translations)?.name }}
                  </h3>
                </div>
                <div class="grid grid-cols-[0fr] w-full transition-all group-hover:grid-cols-[1fr]">
                  <div class="grid grid-cols-[1fr_min-content] min-w-0 items-center gap-2rem overflow-hidden bg-neutral-white/60">
                    <div class="h-px w-full bg-black" />
                    <IconArrowRight class="mr-2rem size-2.5rem" />
                  </div>
                </div>
              </div>
              <ul v-if="dt(cover.listing_translations)?.teaser_description.length" class="px-2rem py-1rem">
                <li v-for="item, index in dt(cover.listing_translations)?.teaser_description" :key="index" class="flex gap-4">
                  <div class="mt-.8rem h-px w-3rem flex-shrink-0 bg-black" />
                  <p class="font-body text-balance text-1.2rem leading-2.2rem">
                    {{ item.text }}
                  </p>
                </li>
              </ul>
            </NuxtLinkLocale>
          </template>
        </div>
      </Container>
    </div>
  </BlockBase>
</template>

<style scoped>
.bg-gradient {
  background-image: linear-gradient(to bottom, transparent 0%, #000b1a 50%);
}
</style>
